import {action, makeAutoObservable, runInAction} from 'mobx';
import {AjaxContentType, ApiService} from '../Services';
import {
    ApiURL,
    DEVICE_COMPONENTS,
    MAINTENANCE_INSPECTION_NAME,
    MAINTENANCE_REVISION_NAME, Roles,
} from '../AppConstants';
import {
    CommentTDO,
    ComponentTDO,
    ConditionChartDataDTO,
    ConditionDTO,
    EfficiencyChartDataDTO,
    EfficiencyDTO,
    MaintenanceDTO,
    MaintenanceStatus,
    MeasurementDTO,
    NewMaintenanceDTO, RegionUserDTO,
    ServiceChangeKey,
    SupportTicketDTO,
    TaskType,
    User,
    UserDTO, UserPermission,
    UserPermissionsDTO,
} from '../Models/ApiModels';
import {RangePickerStore} from '../Components/RangePicker/RangePicker';
import {FiltersStore} from './FiltersStore';
import {createContext} from 'react';
import {PromiseCompletion} from '../Services/PromiseCompletion';
import DateTimeService from '../Services/DateTimeService';
import {ConfirmationStore} from "../Components/ConfirmationDialog/ConfirmationDialog";
import {ValueChangedInfo} from "devextreme/ui/editor/editor";
import {authService} from "../index";

type TokenClaims = {
    sub?: string;
    name?: string;
    [key: string]: any;
}

type EfficiencyProps = {
    organizationId: string,
    deviceId?: string[],
    siteId?: string[],
    timeFrom?: Date,
    timeTo?: Date,
}
type TicketsParams = {
    organizationId: string,
    siteId: string,
    regionId: string,
    deviceId: string,
    createdOnFrom?: Date | string
    createdOnTo?: Date | string
    modifiedOnFrom?: Date | string
    modifiedOnTo?: Date | string
    resolvedOnFrom?: Date | string
    resolvedOnTo?: Date | string
}
type UserPermissionsParams = {
    userId?: string
    organizationId?: string
    regionId?: string
    deviceId?: string
    siteId?: string
}

export const DefaultServiceRange = [DateTimeService.addDays(DateTimeService.todayLocal(), -5), DateTimeService.endOfDay(DateTimeService.todayLocal())];

export class AppStore {
    public resetGridVisible: boolean = false;
    public conditions: ConditionChartDataDTO | null = null;
    public conditionsLoader: PromiseCompletion = new PromiseCompletion();

    public efficiency: EfficiencyChartDataDTO | null = null;
    public efficiencyLoader: PromiseCompletion = new PromiseCompletion();

    public rangeStore = new RangePickerStore();
    public filtersStore = new FiltersStore();
    public confirmationStore = new ConfirmationStore();
    public siteLoader: PromiseCompletion = new PromiseCompletion();
    public ticketsUsers: UserDTO[] = [];
    public regionsUsers: RegionUserDTO[] = [];
    public appUsers: UserDTO[] = [];
    public userPermissions: UserPermissionsDTO[] = [];
    public tickets: SupportTicketDTO[] = [];
    public checklist: MaintenanceDTO[] = [];
    public logs: MaintenanceDTO[] = [];
    public components: Map<string, ComponentTDO[]> = new Map();
    public measurements: MeasurementDTO[] = [];
    public serviceLoader: PromiseCompletion = new PromiseCompletion();
    public administrationLoader: PromiseCompletion = new PromiseCompletion();
    public drawerLoader: PromiseCompletion = new PromiseCompletion();
    public userUpdatedFilter: string = 'All';
    public serviceUpdatedFilter: ServiceChangeKey = ServiceChangeKey.isNotResolved;
    public serviceRangeFilter: Array<Date | string> = DefaultServiceRange;
    public selectedServiceRange: Array<Date | string> = DefaultServiceRange;
    public isAdmin = authService.isAdmin;
    public currentUser = authService.currentUser;
    public isCompanyAdmin = authService.isCompanyAdmin;

    public attachmentsData: Map<string, string> = new Map();

    constructor() {
        makeAutoObservable(this);
        void this.filtersStore.getData();
    }

    public resetConditions = () => {
        this.conditions = null;
    };

    public async loadUserPermissions(organizationId?: string) {
        const params: UserPermissionsParams = {
            userId: this.userUpdatedFilter === 'All' ? undefined : this.userUpdatedFilter,
            organizationId: this.isAdmin ? organizationId : undefined,
        };
        const {data: appUsersPermissionsData} = await ApiService.getData<UserPermissionsDTO[]>(ApiURL.Permissions, params, {completion: this.administrationLoader});

        const data = appUsersPermissionsData.map((perm) => {
            const user = this.appUsers.find(us => us.id === perm.userId);

            if(user){
                const customerName = this.filtersStore.filters.customers.find(cust => cust.id === perm.organizationId)?.name;
                const displayNameArr = user?.displayName.split(' ');

                return ({
                    id: perm.id,
                    user: perm.id,
                    userDisplayName: user?.displayName,
                    familyName: displayNameArr?.[1],
                    givenName: displayNameArr?.[0],
                    entity: customerName,
                    email: user?.email,
                    customer: perm.organizationId,
                    region: perm.regionId,
                    site: perm.siteId,
                    plant: perm.deviceId
                })
            }
            else return null;
        }).filter(Boolean) as UserPermission[];

        runInAction(() => {
            this.userPermissions = this.userUpdatedFilter !== 'All' ? data.filter((it: UserPermission) => it.id === this.userUpdatedFilter) : data;
        });

    }

    public async loadAppUsers(organizationId: string, role?: Roles) {
        const params = {organizationId: this.isAdmin ? organizationId : undefined, role }

        const { data: appUsers } = await ApiService.getData<UserDTO[]>(ApiURL.Users, params);

        runInAction(() => {
            this.appUsers = appUsers;
        });
    }

    public async loadUsers(deviceId: string) {
        if (!this.filtersStore.selected.customer) {
            this.ticketsUsers = [];
            return;
        }
        const ticketsUsersData = ApiService.getData<UserDTO[]>(`${ApiURL.Devices}/${deviceId}${ApiURL.Permissions}`, null);

        this.serviceLoader.subscribe(ticketsUsersData);
        const {data: ticketsUsers} = await ticketsUsersData;
        runInAction(() => {
            const claims = this.currentUser?.idTokenClaims as TokenClaims | undefined;

            this.ticketsUsers = this.isCompanyAdmin ? [...ticketsUsers, {id: claims?.sub, userId: claims?.sub, displayName: this.currentUser?.name ?? claims?.name} as UserDTO] : ticketsUsers;
        });
    }

    public async loadTickets() {
        if (!this.filtersStore.selected.customer) {
            this.tickets = [];
            return;
        }
        try {
            const params: TicketsParams = {
                organizationId: this.filtersStore.selected.customer,
                siteId: this.filtersStore.selected.site,
                regionId: this.filtersStore.selected.region,
                deviceId: this.filtersStore.selected.plant,
            };
            const {data: ticketsResp} = await ApiService.getData<SupportTicketDTO[]>(ApiURL.Tickets, params, {completion: this.serviceLoader});
            runInAction(() => {
                this.tickets = ticketsResp?.filter?.(it => this.serviceUpdatedFilter === ServiceChangeKey.isNotResolved ?  !it.isResolved : Boolean(it.isResolved));
            });
        } catch (e) {
            console.log(e);
        }
    }

    public async loadTasks() {
        if (!this.filtersStore.selected.customer || !this.filtersStore.selected.plant) {
            this.checklist = [];
            this.logs = [];
            return;
        }
        try {
            const params = {
                organizationId: this.filtersStore.selected.customer,
                siteId: [this.filtersStore.selected.site],
                deviceId: [this.filtersStore.selected.plant]
            };
            const checklistResp = ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, params);
            const logsResp = ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, {
                ...params,
                status: MaintenanceStatus.Completed
            });
            const requests = Promise.all([checklistResp, logsResp]);
            this.serviceLoader.subscribe(requests);
            const [{data: checklistData}, {data: logsData}] = await requests;
            runInAction(() => {
                this.checklist = checklistData;
                this.logs = logsData;
            });
        } catch (e) {
            console.log(e);
        }
    }

    public async loadRegionUsers() {
        const regionUsers: RegionUserDTO[][] = await Promise.all(
            this.filtersStore.filters.regions.map(async (region) => {
                const { data: users } = await ApiService.getData<User[]>(
                    `${ApiURL.Regions}/${region.id}${ApiURL.Permissions}`,
                    null,
                    { completion: this.serviceLoader }
                );

                return users.map((user) => ({
                    ...user,
                    regionId: region.id,
                }));
            })
        );
            runInAction(() => {
                this.regionsUsers = regionUsers.flat();
            });
        }

    public async loadMeasurements() {
        if (!this.filtersStore.selected.plant) {
            this.measurements = [];
            return;
        }
        try {
            const {data: checklistResp} = await ApiService.getData<MeasurementDTO[]>(`${ApiURL.Devices}/${this.filtersStore.selected.plant}/measurements`);
            this.measurements = checklistResp;
        } catch (e) {
            console.log(e);
        }
    }

    public async loadChatData(ticketId: string) {
        const {data} = await ApiService.getData<CommentTDO[]>(`${ApiURL.Tickets}/${ticketId}/comments`, {}, {completion: this.drawerLoader});
        const commentList = data.map(e => {
            // const userName = this.ticketsUsers.find(u => u.id === e.userId)?.displayName;
            return {...e, createdOn: new Date(e.createdOn)};
        }).sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());
        commentList.filter(e => !!e.attachments.length).forEach(e => {
            e.attachments.forEach(a => {
                if (!this.attachmentsData.get(a.id)) {
                    void new Promise(async () => {
                        const {data: fileData} = await ApiService.getData(`${ApiURL.Tickets}/${ticketId}/comments/${e.id}/attachments/${a.id}`, {},
                            {
                                responseType: 'blob'
                            });
                        runInAction(() => {
                            this.attachmentsData.set(a.id, URL.createObjectURL(new Blob([fileData as BlobPart], {type: 'application/octet-stream'})));
                        });
                    });
                }
            });
        });
        return commentList;
    }

    public get attachmentKeys() {
        return Array.from(this.attachmentsData.keys());
    }

    public async saveMessage(id: string, message?: string) {
        if (message !== undefined) {
            return ApiService.postData<CommentTDO[]>(
                `${ApiURL.Tickets}/${id}/comments`,
                {content: message},
                {completion: this.drawerLoader});
        }
    }

    public async saveCommentFile(ticketsId: string, commentId: string, file: File) {
        const formData = new FormData();
        formData.append('file', file);

        return ApiService.postData<CommentTDO[]>(
            `${ApiURL.Tickets}/${ticketsId}/comments/${commentId}/attachments`,
            formData,
            {
                contentType: AjaxContentType.FORM_DATA,
                completion: this.drawerLoader
            });
    }

    public async loadComponents(id?: string) {
        const deviceId = id || this.filtersStore.selected.plant;
        if (deviceId && !this.components.get(deviceId)) {
            //TODO: Use API when it will be fixed
            // const {data} = await ApiService.getData<ComponentTDO[]>(`${ApiURL.Devices}/${deviceId}/components`, {}, {completion: this.drawerLoader});
            this.components.set(deviceId, DEVICE_COMPONENTS);
        }
    }

    public async deleteTask(taskId: string) {
        await ApiService.deleteData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}/${taskId}`, {completion: this.drawerLoader});
    }

    public async saveTask(task: MaintenanceDTO) {
        const {plant, customer} = this.filtersStore.selected;
        const params: NewMaintenanceDTO = {
            description: task?.description,
            deviceId: plant,
            organizationId: customer,
            isRecurring: !!task?.isRecurring,
        };
        if (task.type === TaskType.data) {
            params.measurement = task.measurement;
            params.valueDelta = task.valueDelta;
        }
        if (task.type === TaskType.scheduled) {
            params.timeDelta = `P${task?.timeD??0}DT${task?.timeH??0}H`;
            if (task.initialTime) {
                params.initialTime = DateTimeService.toJSONLocal(task.initialTime);
            }
        }


        if (task.taskId) {
            params.status = task.status;
            params.logMessage = task?.userLog?.message;
            if (task.type === TaskType.data) {
                params.value = task.value;
            }
            await ApiService.patchData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}/${task.taskId}`, params, {completion: this.drawerLoader});
        } else {
            await ApiService.postData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}`, params, {completion: this.drawerLoader});
        }
    }

    public async saveTicket(ticket: SupportTicketDTO, deletedFiles: string[], files: File[], comments: CommentTDO[]) {
        ticket.componentIds = ticket.componentId ? [ticket.componentId] : [];
        ticket.componentId = undefined;

        let reqList: Array<Promise<void>> = [];
        let ticketId = ticket.id;

        const _uploader = (ticketId: string, file: File) => {
            return new Promise<void>(async resolve => {
                const resp = await this.saveMessage(ticketId, '');
                const commentId = ((resp as any).headers.location).split('/').reverse()[0];
                if (commentId) {
                    await this.saveCommentFile(ticketId, commentId, file);
                    resolve();
                }
            });
        };

        if (ticketId) {
            await ApiService.patchData<SupportTicketDTO>(`${ApiURL.Tickets}/${ticketId}`,
                {
                    userId: !ticket.userId ? undefined : ticket.userId, //ticket.userId === ticket.user?.displayName ? undefined : ticket.userId,
                    componentIds: ticket.componentIds,
                    status: ticket.status,
                    description: ticket.description,
                }, {completion: this.drawerLoader});
        } else {
            const newTicketResp = await ApiService.postData<SupportTicketDTO>(`${ApiURL.Tickets}`, ticket, {completion: this.drawerLoader});
            ticketId = ((newTicketResp as any).headers.location).split('/').reverse()[0];
        }

        runInAction(async () => {
            deletedFiles.forEach((fileId) => {
                const comment = comments.find(c => c.attachments.length && c.attachments[0].id === fileId);
                if (comment) {
                    reqList.push(ApiService.deleteData(`${ApiURL.Tickets}/${ticketId}/comments/${comment.id}/attachments/${fileId}`).then(() => {
                    }));
                }
            });
            files.forEach((file) => {
                reqList.push(_uploader(ticketId, file));
            });

            const saveAll = Promise.all(reqList);
            this.drawerLoader.subscribe(saveAll);
            await saveAll;
        });
    }

    public async saveRegion(organizationId: string, name: string){
        if(organizationId){
            await ApiService.postData<UserPermissionsDTO>(`${ApiURL.Regions}`, {organizationId, name}, {completion: this.drawerLoader});
        }
    }

    public async saveUserPermission(permission: UserPermission, isEdit?: boolean) {
        const bodyData = {
            id: isEdit ? permission.id : undefined,
            userId: permission.id,
            organizationId: permission?.customer,
            regionId: permission?.region && permission?.region !== 'All' ? permission.region : undefined,
            siteId: permission?.site && permission?.site !== 'All' ? permission.site : undefined,
            deviceId: permission?.plant && permission?.plant !== 'All' ? permission.plant : undefined,
        };
        if (!isEdit) {

            await ApiService.postData<UserPermissionsDTO>(`${ApiURL.Permissions}`, bodyData, {completion: this.drawerLoader});
        }
        else {
            await ApiService.putData<UserPermissionsDTO>(`${ApiURL.Permissions}/${permission.id}`, bodyData, {completion: this.drawerLoader});
        }
    }

    @action
    public deleteUserPermission = async (permission: UserPermissionsDTO) => {
        this.userPermissions = this.userPermissions.filter(it => it.id !== permission.id );
        await ApiService.deleteData<UserPermissionsDTO>(`${ApiURL.Permissions}/${permission.id}`, {completion: this.drawerLoader});
    }

    public async loadConditions() {
        if (!this.filtersStore.selected.customer || !this.filtersStore.selected.plant) {
            this.conditions = null;
            return;
        }
        try {
            const diff = this.rangeStore.dateTo.getTime() - this.rangeStore.dateFrom.getTime();
            const props = {
                organizationId: this.filtersStore.selected.customer,
                siteId: [this.filtersStore.selected.site],
                deviceId: [this.filtersStore.selected.plant]
            };
            const currentProps = {
                ...props,
                timeFrom: this.rangeStore.dateFrom,
                timeTo: this.rangeStore.dateTo,
            };
            const prevProps = {
                ...props,
                timeFrom: DateTimeService.addMs(this.rangeStore.dateFrom, -diff),
                timeTo: DateTimeService.addMs(this.rangeStore.dateTo, -diff),
            };

            const inspectionProps = {
                ...props,
                name: MAINTENANCE_INSPECTION_NAME
            };
            const revisionProps = {
                ...props,
                name: MAINTENANCE_REVISION_NAME
            };

            const currentData = ApiService.getData<ConditionDTO>(ApiURL.Condition, currentProps);
            const prevData = ApiService.getData<ConditionDTO>(ApiURL.Condition, prevProps);
            const inspectionData = ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, inspectionProps);
            const revisionData = ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, revisionProps);

            const requests = Promise.all([currentData, prevData, inspectionData, revisionData]);
            this.conditionsLoader.subscribe(requests);
            const [{data: current}, {data: historical}, {data: inspection}, {data: revision}] = await requests;

            runInAction(() => {
                 this.conditions = {
                     current,
                     historical,
                     inspection,
                    revision,
                 } as unknown as ConditionChartDataDTO;
             });
        } catch (e) {
            console.log(e);
        }
    }

    @action
    public resetEfficiency = () => {
        this.efficiency = null;
    };

    @action
    public onServiceRangeChange = (data: ValueChangedInfo) => {
        const from = data.value[0] ? data.value[0] : '';
        const to = data.value[1] ? DateTimeService.endOfDay(data.value[1]) : '';
        this.selectedServiceRange = [from, to];
    };

    @action
    public onServiceRangeClosed = () => {
        const needRefresh = JSON.stringify(this.serviceRangeFilter) !== JSON.stringify(this.selectedServiceRange);
        this.serviceRangeFilter = this.selectedServiceRange;
        if (needRefresh) {
            void this.loadTickets();
        }
    };

    // @action
    // public onUserChange = (value: string) => {
    //     this.userUpdatedFilter = value;
    //     void this.loadUserPermissions(this.filtersStore.selected.customer);
    // };

    @action
    public onServiceUpdatedChange = (value: ServiceChangeKey) => {
        this.serviceUpdatedFilter = value;
        void this.loadTickets();
    };

    public async loadEfficiency() {
        if (!this.filtersStore.selected.customer || !this.filtersStore.selected.site) {
            this.efficiency = null;
            return;
        }
        try {
            const diff = this.rangeStore.dateTo.getTime() - this.rangeStore.dateFrom.getTime();
            let props: EfficiencyProps = {
                organizationId: this.filtersStore.selected.customer,
            };
            if (this.filtersStore.selected.site) {
                props.siteId = [this.filtersStore.selected.site];
            }
            if (this.filtersStore.selected.plant) {
                props.deviceId = [this.filtersStore.selected.plant];
            }
            const currentProps = {
                ...props,
                timeFrom: this.rangeStore.dateFrom,
                timeTo: this.rangeStore.dateTo,
            };
            const prevProps = {
                ...props,
                timeFrom: DateTimeService.addMs(this.rangeStore.dateFrom, -diff),
                timeTo: DateTimeService.addMs(this.rangeStore.dateTo, -diff),
            };

            const currentData = ApiService.getData<EfficiencyDTO>(ApiURL.Efficiency, currentProps);
            const prevData = ApiService.getData<EfficiencyDTO>(ApiURL.Efficiency, prevProps);

            const requests = Promise.all([currentData, prevData]);
            this.efficiencyLoader.subscribe(requests);
            const [{data: current}, {data: historical}] = await requests;
            runInAction(() => {
                this.efficiency = {
                    current,
                    historical,
                };
            });
        } catch (e) {
            console.log(e);
        }
    }

    @action
    public showGridReset = () => {
        this.resetGridVisible = true;
    };

    @action
    public hideGridReset = () => {
        this.resetGridVisible = false;
    };

    public convertToTicket = (_: MaintenanceDTO) => {
        return;
    };
}

export const AppStoreContext = createContext({} as AppStore);
